/**
 * Predictions (predictions.md, M11): a Monte Carlo projection of available money and net worth, the month-end
 * forecast per category, goal probabilities and runway. Everything here is read-only and never enters the budget.
 *
 * The model is a seasonal, recency-weighted bootstrap of whole historical months: every future month of every path
 * re-uses one real past month's unscheduled income and spending, so the link between income and spending within a
 * month survives. Payments that are already known (schedules, loan installments) are added exactly on top.
 */
import { addMonths, lastDay, monthOf, monthsBetween, type IsoDate, type Month } from './dates';
import { amortize, type InterestMethod, type LoanRate } from './loan';
import { convert, type Currency } from './money';

/** Growth and percent changes are percent × 10 000, like loan rates (3,1 % = 31 000). */
export const GROWTH_SCALE = 10_000;
export const HORIZONS = [6, 12, 24, 60] as const;
export type Horizon = (typeof HORIZONS)[number];
export const BASED_ON = [12, 24, 36] as const;
export type BasedOn = (typeof BASED_ON)[number];
/** Months of history after which the draw keeps the seasons (August from around August). */
export const SEASONAL_MIN_MONTHS = 24;
/** Below this many months the projection is a rough guess and says so. */
export const MIN_HISTORY_MONTHS = 6;
/** Recent months weigh more: a month a year older counts half as much. */
export const HALF_LIFE_MONTHS = 12;
/** NBS's inflation target midpoint, used before the first Eurostat fetch. */
export const NBS_TARGET_INFLATION = 3 * GROWTH_SCALE;

export interface HistoryMonth {
  month: Month;
  /** Income not covered by a schedule (base). */
  income: number;
  /** Spending not covered by a schedule, per category group id (base, positive = money out). */
  spend: Record<string, number>;
  /** Unscheduled transfers to tracking accounts (savings, extra loan payments): leave available money, stay in net worth. */
  moved: number;
}

/** One known future occurrence (schedules), already in base currency at today's rate. */
export interface KnownFlow {
  date: IsoDate;
  /** Effect on available money (signed). */
  available: number;
  /** Effect on net worth (signed). A transfer to a savings account: available −X, net worth 0. */
  netWorth: number;
  /** Category group of an outflow, for runway's essential spending. */
  groupId: string | null;
  label: string;
}

export interface LoanModel {
  accountId: string;
  name: string;
  currency: Currency;
  /** Native → base at today's rate (RATE_SCALE). */
  rate: number;
  /** Remaining debt, native, positive. */
  balance: number;
  rates: LoanRate[];
  installment: number;
  method: InterestMethod;
  nextPayment: IsoDate;
  /** The installment is paid from an on-budget account, so it reduces available money. */
  fromBudget: boolean;
}

export interface MonthCategory {
  id: string;
  name: string;
  groupId: string;
  /** Available now (base). */
  available: number;
  /** Spent this month so far (base, positive). */
  spent: number;
  /** Scheduled outflows still to come this month (base, positive). */
  scheduledRest: number;
  /** Unscheduled spending after today's day-of-month in each past complete month, newest first (positive). */
  pastRest: number[];
}

export interface PredictionAssumptions {
  basedOn: BasedOn;
  /** Annual income growth (GROWTH_SCALE). */
  incomeGrowth: number;
  /** Annual spending growth: Serbia's inflation, the household's own trend, or a typed value (GROWTH_SCALE). */
  spendGrowth: { source: 'inflation' | 'own' | 'manual'; manual: number };
  /** Safety buffer in base; null = one month of essential spending. */
  buffer: number | null;
  /** History months left out of the draw (the month you bought the car). */
  excludedMonths: Month[];
  /** Essential groups for runway; null = the default (everything that isn't savings or investments). */
  essentialGroupIds: string[] | null;
}

export const DEFAULT_ASSUMPTIONS: PredictionAssumptions = {
  basedOn: 24,
  incomeGrowth: 0,
  spendGrowth: { source: 'inflation', manual: NBS_TARGET_INFLATION },
  buffer: null,
  excludedMonths: [],
  essentialGroupIds: null,
};

export type ScenarioChange =
  | { id: string; kind: 'oneOff'; label: string; month: Month; amount: number }
  | { id: string; kind: 'recurring'; label: string; from: Month; to: Month | null; amount: number }
  | { id: string; kind: 'income'; label: string; from: Month; percent: number }
  | { id: string; kind: 'group'; label: string; groupId: string; from: Month; percent: number }
  | {
      id: string;
      kind: 'loanExtra';
      label: string;
      accountId: string;
      /** Native loan currency. */
      monthly: number;
      oneOff: { month: Month; amount: number } | null;
    };
export const SCENARIO_KINDS = ['oneOff', 'recurring', 'income', 'group', 'loanExtra'] as const;

export interface Scenario {
  id: string;
  name: string;
  changes: ScenarioChange[];
  updatedAt: string;
  updatedBy: string | null;
}

export interface PredictionModelDto {
  today: IsoDate;
  month: Month;
  start: {
    available: number;
    otherAssets: number;
    /** Debts that aren't projected loans (liabilities without terms), negative. */
    otherDebts: number;
  };
  /** Month-end values of the last 12 months, the current month as of today. */
  actual: { month: Month; available: number; netWorth: number }[];
  groups: { id: string; name: string; essential: boolean }[];
  /** Complete months, oldest first, up to 36. */
  history: HistoryMonth[];
  /** Schedule occurrences after today, up to 60 months ahead, loan installments excluded. */
  known: KnownFlow[];
  loans: LoanModel[];
  thisMonth: { day: number; days: number; categories: MonthCategory[] };
  /** Savings targets with a due date, to prefill the goal check. */
  targets: { categoryId: string; name: string; amount: number; dueDate: IsoDate; available: number }[];
  inflation: { rate: number; month: Month | null; source: 'eurostat' | 'nbs_target' };
  /** Year-over-year change of the household's unscheduled spending, when there are 24 months. */
  ownGrowth: number | null;
  assumptions: PredictionAssumptions;
  scenarios: Scenario[];
}

// ───────── Randomness ─────────

/** mulberry32: small, fast, seeded. The same seed gives the same chart on every render. */
export function rng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4_294_967_296;
  };
}

/** Linear-interpolated quantile of a sorted array (q in 0..1). */
export function quantile(sorted: ArrayLike<number>, q: number): number {
  const n = sorted.length;
  if (!n) return 0;
  const pos = Math.min(1, Math.max(0, q)) * (n - 1);
  const lo = Math.floor(pos);
  const hi = Math.min(n - 1, lo + 1);
  return sorted[lo]! + (sorted[hi]! - sorted[lo]!) * (pos - lo);
}

/** Weighted quantile: the smallest value whose cumulative weight reaches q. */
export function weightedQuantile(values: number[], weights: number[], q: number): number {
  const idx = values.map((_, i) => i).sort((a, b) => values[a]! - values[b]!);
  const total = weights.reduce((s, w) => s + w, 0);
  let acc = 0;
  for (const i of idx) {
    acc += weights[i]!;
    if (acc >= q * total - 1e-9) return values[i]!;
  }
  return values[idx[idx.length - 1]!] ?? 0;
}

// ───────── Assumption helpers ─────────

export const spendGrowthOf = (
  m: Pick<PredictionModelDto, 'inflation' | 'ownGrowth'>,
  a: PredictionAssumptions,
) =>
  a.spendGrowth.source === 'manual'
    ? a.spendGrowth.manual
    : a.spendGrowth.source === 'own' && m.ownGrowth !== null
      ? m.ownGrowth
      : m.inflation.rate;

/** The history the draw uses: the last `basedOn` complete months, minus excluded ones. */
export function usedHistory(
  m: Pick<PredictionModelDto, 'history'>,
  a: PredictionAssumptions,
): HistoryMonth[] {
  const ex = new Set(a.excludedMonths);
  return m.history.slice(-a.basedOn).filter((h) => !ex.has(h.month));
}

export function essentialGroups(
  m: Pick<PredictionModelDto, 'groups'>,
  a: PredictionAssumptions,
): Set<string> {
  return new Set(a.essentialGroupIds ?? m.groups.filter((g) => g.essential).map((g) => g.id));
}

/** Months with the most unscheduled spending, as one-click exclusion suggestions. */
export function outlierMonths(history: HistoryMonth[], n = 3): { month: Month; spend: number }[] {
  return history
    .map((h) => ({ month: h.month, spend: Object.values(h.spend).reduce((s, v) => s + v, 0) + h.moved }))
    .sort((a, b) => b.spend - a.spend)
    .slice(0, n);
}

const totalSpend = (h: HistoryMonth, only?: Set<string>) =>
  Object.entries(h.spend).reduce((s, [g, v]) => (!only || only.has(g) ? s + v : s), 0);

const growth = (annual: number, k: number) => (1 + annual / GROWTH_SCALE / 100) ** (k / 12);

// ───────── Loans ─────────

export interface LoanPath {
  /** Installments from the budget per month index (base, positive = money out), incl. extras and one-offs. */
  paid: number[];
  /** Debt at each month end (base, negative). */
  debt: number[];
  payoff: IsoDate | null;
  totalInterest: number;
}

/** A loan's month-by-month payments and remaining debt, with a scenario's extra payments. */
export function loanPath(
  loan: LoanModel,
  month: Month,
  horizon: number,
  extra?: { monthly: number; oneOff: { month: Month; amount: number } | null },
): LoanPath {
  const oneOff = extra?.oneOff && extra.oneOff.amount > 0 ? extra.oneOff : null;
  const am = amortize({
    balance: loan.balance,
    rates: loan.rates,
    installment: loan.installment,
    method: loan.method,
    firstPayment: loan.nextPayment,
    extraMonthly: extra?.monthly ?? 0,
    oneOff: oneOff ? { date: `${oneOff.month}-01`, amount: oneOff.amount } : null,
    maxMonths: horizon + 2 + monthsBetween(month, monthOf(loan.nextPayment)),
  });
  const paid = new Array<number>(horizon + 1).fill(0);
  const debt = new Array<number>(horizon + 1).fill(0);
  let bal = loan.balance;
  let r = 0;
  for (let k = 0; k <= horizon; k++) {
    const end = lastDay(addMonths(month, k));
    const before = bal;
    while (r < am.rows.length && am.rows[r]!.date <= end) {
      paid[k]! += am.rows[r]!.payment;
      bal = am.rows[r]!.balance;
      r++;
    }
    if (oneOff && oneOff.month === addMonths(month, k)) paid[k]! += Math.min(oneOff.amount, before);
    debt[k] = bal ? -convert(bal, loan.rate) : 0;
    paid[k] = loan.fromBudget ? convert(paid[k]!, loan.rate) : 0;
  }
  return { paid, debt, payoff: am.payoffDate, totalInterest: am.totalInterest };
}

// ───────── Simulation ─────────

export interface Band {
  p5: number;
  p10: number;
  p25: number;
  p50: number;
  p75: number;
  p90: number;
  p95: number;
}

export interface Simulation {
  /** Month index 0 = the current month's end, then one per month. */
  months: Month[];
  paths: number;
  /** Row-major paths × months. */
  available: Float64Array;
  netWorth: Float64Array;
  /** Lowest month-end value of each path. */
  minAvailable: Float64Array;
  minNetWorth: Float64Array;
  bands: { available: Band[]; netWorth: Band[] };
  historyMonths: number;
  seasonal: boolean;
  loans: { accountId: string; payoff: IsoDate | null; totalInterest: number }[];
}

export interface SimOptions {
  horizon: number;
  paths?: number;
  seed?: number;
  scenario?: Scenario | null;
}

/** The pools future months are drawn from, with recency weights; seasonal pools when there's enough history. */
function drawPools(history: HistoryMonth[], month: Month) {
  const weight = (h: HistoryMonth) =>
    0.5 ** (Math.max(0, monthsBetween(h.month, month) - 1) / HALF_LIFE_MONTHS);
  const cumulative = (idx: number[]) => {
    const c: number[] = [];
    let acc = 0;
    for (const i of idx) c.push((acc += weight(history[i]!)));
    return { idx, c, total: acc };
  };
  const all = cumulative(history.map((_, i) => i));
  const seasonal = history.length >= SEASONAL_MIN_MONTHS;
  const byCal = seasonal
    ? Array.from({ length: 12 }, (_, cal) =>
        cumulative(
          history
            .map((h, i) => [Number(h.month.slice(5, 7)) - 1, i] as const)
            .filter(([c]) => (c - cal + 12) % 12 <= 1 || (cal - c + 12) % 12 <= 1)
            .map(([, i]) => i),
        ),
      )
    : null;
  const pick = (pool: { idx: number[]; c: number[]; total: number }, u: number) => {
    const x = u * pool.total;
    let lo = 0;
    let hi = pool.c.length - 1;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (pool.c[mid]! < x) lo = mid + 1;
      else hi = mid;
    }
    return pool.idx[lo]!;
  };
  return {
    seasonal,
    draw(next: () => number, cal: number): number {
      const pool = byCal && next() < 0.5 && byCal[cal]!.idx.length ? byCal[cal]! : all;
      return pick(pool, next());
    },
  };
}

/** Per-month-index constants of a run: growth, scenario factors, known flows and loans. */
function monthPlan(
  model: PredictionModelDto,
  a: PredictionAssumptions,
  horizon: number,
  scenario: Scenario | null,
) {
  const H = horizon;
  const months = Array.from({ length: H + 1 }, (_, k) => addMonths(model.month, k));
  const gI = months.map((_, k) => growth(a.incomeGrowth, k));
  const gS = months.map((_, k) => growth(spendGrowthOf(model, a), k));
  const incomeF = months.map(() => 1);
  const groupF: Record<string, number>[] = months.map(() => ({}));
  const knownA = new Array<number>(H + 1).fill(0);
  const knownNW = new Array<number>(H + 1).fill(0); // net worth effect beyond available money
  const at = (date: IsoDate) => monthsBetween(model.month, monthOf(date));
  for (const f of model.known) {
    const k = at(f.date);
    if (k < 0 || k > H || f.date <= model.today) continue;
    knownA[k]! += f.available;
    knownNW[k]! += f.netWorth - f.available;
  }
  const changes = scenario?.changes ?? [];
  for (const c of changes) {
    for (let k = 0; k <= H; k++) {
      const m = months[k]!;
      if (c.kind === 'oneOff' && c.month === m) knownA[k]! += c.amount;
      else if (c.kind === 'recurring' && c.from <= m && (!c.to || m <= c.to)) knownA[k]! += c.amount;
      else if (c.kind === 'income' && c.from <= m) incomeF[k]! *= 1 + c.percent / GROWTH_SCALE / 100;
      else if (c.kind === 'group' && c.from <= m)
        groupF[k]![c.groupId] = (groupF[k]![c.groupId] ?? 1) * (1 + c.percent / GROWTH_SCALE / 100);
    }
  }
  const loanPaid = new Array<number>(H + 1).fill(0);
  const loanDebt = new Array<number>(H + 1).fill(0);
  const loans = model.loans.map((l) => {
    const ex = changes.find(
      (c): c is Extract<ScenarioChange, { kind: 'loanExtra' }> =>
        c.kind === 'loanExtra' && c.accountId === l.accountId,
    );
    const p = loanPath(l, model.month, H, ex ? { monthly: ex.monthly, oneOff: ex.oneOff } : undefined);
    for (let k = 0; k <= H; k++) {
      loanPaid[k]! += p.paid[k]!;
      loanDebt[k]! += p.debt[k]!;
    }
    return { accountId: l.accountId, payoff: p.payoff, totalInterest: p.totalInterest };
  });
  return { months, gI, gS, incomeF, groupF, knownA, knownNW, loanPaid, loanDebt, loans };
}

function bandsOf(values: Float64Array, paths: number, cols: number): Band[] {
  const out: Band[] = [];
  const col = new Float64Array(paths);
  for (let k = 0; k < cols; k++) {
    for (let p = 0; p < paths; p++) col[p] = values[p * cols + k]!;
    col.sort();
    const q = (x: number) => Math.round(quantile(col, x));
    out.push({
      p5: q(0.05),
      p10: q(0.1),
      p25: q(0.25),
      p50: q(0.5),
      p75: q(0.75),
      p90: q(0.9),
      p95: q(0.95),
    });
  }
  return out;
}

/** Run the Monte Carlo projection. Deterministic for a given seed. */
export function simulate(model: PredictionModelDto, a: PredictionAssumptions, opts: SimOptions): Simulation {
  const H = Math.max(0, Math.floor(opts.horizon));
  const P = Math.max(1, Math.floor(opts.paths ?? 2000));
  const cols = H + 1;
  const next = rng(opts.seed ?? 20_261_103);
  const history = usedHistory(model, a);
  const pools = drawPools(history, model.month);
  const plan = monthPlan(model, a, H, opts.scenario ?? null);
  // The current month is partly over: draw only the remaining share of a month.
  const rest0 = Math.max(0, model.thisMonth.days - model.thisMonth.day) / model.thisMonth.days;
  const cal = plan.months.map((m) => Number(m.slice(5, 7)) - 1);
  const available = new Float64Array(P * cols);
  const netWorth = new Float64Array(P * cols);
  const minAvailable = new Float64Array(P);
  const minNetWorth = new Float64Array(P);
  const fixedNW = model.start.otherAssets + model.start.otherDebts;
  for (let p = 0; p < P; p++) {
    let avail = model.start.available;
    let extra = 0; // money moved into tracking accounts, plus other net-worth-only flows
    let minA = Infinity;
    let minN = Infinity;
    for (let k = 0; k < cols; k++) {
      if (history.length) {
        const h = history[pools.draw(next, cal[k]!)]!;
        const frac = k === 0 ? rest0 : 1;
        const gf = plan.groupF[k]!;
        let spend = 0;
        for (const g in h.spend) spend += h.spend[g]! * (gf[g] ?? 1);
        const inc = h.income * plan.gI[k]! * plan.incomeF[k]!;
        const flow = Math.round(frac * (inc - spend * plan.gS[k]! - h.moved));
        avail += flow;
        extra += Math.round(frac * h.moved);
      }
      avail += plan.knownA[k]! - plan.loanPaid[k]!;
      extra += plan.knownNW[k]!;
      const nw = avail + fixedNW + extra + plan.loanDebt[k]!;
      available[p * cols + k] = avail;
      netWorth[p * cols + k] = nw;
      if (avail < minA) minA = avail;
      if (nw < minN) minN = nw;
    }
    minAvailable[p] = minA;
    minNetWorth[p] = minN;
  }
  return {
    months: plan.months,
    paths: P,
    available,
    netWorth,
    minAvailable,
    minNetWorth,
    bands: { available: bandsOf(available, P, cols), netWorth: bandsOf(netWorth, P, cols) },
    historyMonths: history.length,
    seasonal: pools.seasonal,
    loans: plan.loans,
  };
}

export type Measure = 'available' | 'netWorth';

/** Share of paths whose lowest month-end value drops below `threshold`. */
export function probBelow(sim: Simulation, measure: Measure, threshold: number): number {
  const mins = measure === 'available' ? sim.minAvailable : sim.minNetWorth;
  let n = 0;
  for (const v of mins) if (v < threshold) n++;
  return n / sim.paths;
}

/** The sorted values of one measure at a month index. */
export function column(sim: Simulation, measure: Measure, k: number): Float64Array {
  const cols = sim.months.length;
  const src = measure === 'available' ? sim.available : sim.netWorth;
  const col = new Float64Array(sim.paths);
  const kk = Math.min(cols - 1, Math.max(0, k));
  for (let p = 0; p < sim.paths; p++) col[p] = src[p * cols + kk]!;
  return col.sort();
}

export interface GoalResult {
  /** Month index of the goal date in the simulation, or null when it is past the horizon. */
  k: number | null;
  probability: number;
  /** Extra saving per month that lifts the chance to `target`; 0 when it is already there, null when impossible. */
  requiredMonthly: number | null;
}

/**
 * Chance of having at least `amount` at the end of the goal's month. Saving s more each month shifts every path
 * by s × months, so the required saving is exact: (amount − quantile(1 − target)) / months.
 */
export function goalCheck(
  sim: Simulation,
  measure: Measure,
  amount: number,
  date: IsoDate,
  target = 0.8,
): GoalResult {
  const k = monthsBetween(sim.months[0]!, monthOf(date));
  if (k < 0 || k >= sim.months.length) return { k: null, probability: 0, requiredMonthly: null };
  const col = column(sim, measure, k);
  let hit = 0;
  for (const v of col) if (v >= amount) hit++;
  const probability = hit / sim.paths;
  if (probability >= target) return { k, probability, requiredMonthly: 0 };
  const need = amount - quantile(col, 1 - target);
  return { k, probability, requiredMonthly: k > 0 ? Math.ceil(need / k) : null };
}

// ───────── Runway ─────────

export interface Runway {
  /** Typical monthly essential spending (unscheduled median + scheduled + loan installments). */
  monthlyEssential: number;
  /** Available money / monthly essential spending. Null when there's no essential spending. */
  months: number | null;
  /** 10th percentile of months until the money runs out with no income, from the same draw. */
  badCase: number | null;
}

const RUNWAY_CAP = 120;

/** How long available money lasts if income stopped today. */
export function runway(
  model: PredictionModelDto,
  a: PredictionAssumptions,
  opts: { paths?: number; seed?: number } = {},
): Runway {
  const history = usedHistory(model, a);
  const ess = essentialGroups(model, a);
  const H = 60;
  const plan = monthPlan(model, a, H, null);
  // Known essential outflows per month (schedules in essential groups) and loan installments.
  const knownOut = new Array<number>(H + 1).fill(0);
  for (const f of model.known) {
    const k = monthsBetween(model.month, monthOf(f.date));
    if (k < 0 || k > H || f.date <= model.today || f.available >= 0 || !f.groupId || !ess.has(f.groupId))
      continue;
    knownOut[k]! -= f.available;
  }
  const knownMonthly =
    (knownOut.slice(1, 13).reduce((s, v) => s + v, 0) +
      plan.loanPaid.slice(1, 13).reduce((s, v) => s + v, 0)) /
    12;
  const essSpend = history.map((h) => totalSpend(h, ess));
  const sorted = [...essSpend].sort((x, y) => x - y);
  const monthlyEssential = Math.round(quantile(sorted, 0.5) + knownMonthly);
  const start = model.start.available;
  if (monthlyEssential <= 0) return { monthlyEssential: 0, months: null, badCase: null };
  const months = Math.max(0, start / monthlyEssential);
  if (!history.length || start <= 0) return { monthlyEssential, months, badCase: start <= 0 ? 0 : months };
  const P = opts.paths ?? 1000;
  const next = rng(opts.seed ?? 7);
  const pools = drawPools(history, model.month);
  const out: number[] = [];
  for (let p = 0; p < P; p++) {
    let money = start;
    let lasted = RUNWAY_CAP;
    for (let k = 1; k <= RUNWAY_CAP; k++) {
      const cal = (Number(model.month.slice(5, 7)) - 1 + k) % 12;
      const h = history[pools.draw(next, cal)]!;
      const outflow =
        totalSpend(h, ess) * growth(spendGrowthOf(model, a), k) +
        (k <= H ? knownOut[k]! + plan.loanPaid[k]! : knownMonthly);
      if (outflow > 0 && money < outflow) {
        lasted = k - 1 + Math.max(0, money) / outflow;
        break;
      }
      money -= outflow;
    }
    out.push(lasted);
  }
  out.sort((x, y) => x - y);
  return { monthlyEssential, months, badCase: quantile(out, 0.1) };
}

/** Default safety buffer: one month of essential spending. */
export const bufferOf = (a: PredictionAssumptions, r: Runway) => a.buffer ?? r.monthlyEssential;

// ───────── This month ─────────

export interface MonthEndRisk {
  id: string;
  name: string;
  available: number;
  spent: number;
  /** Likely spending still to come this month (median), scheduled included. */
  expectedRest: number;
  /** A bad case (90th percentile) of the same. */
  badRest: number;
  /** Likely shortfall at month end (≥ 0). */
  overBy: number;
  /** Chance of running out before the month ends; null when there are under 3 past months to compare. */
  probability: number | null;
  alreadyOver: boolean;
}

/**
 * Which categories will likely run out before the month ends. The spending still to come is drawn from the same
 * category's past months (what was spent after today's day of the month), weighted by recency, plus schedules.
 */
export function monthEndForecast(model: PredictionModelDto, minProbability = 0.3): MonthEndRisk[] {
  const { day, days } = model.thisMonth;
  const out: MonthEndRisk[] = [];
  for (const c of model.thisMonth.categories) {
    const sched = c.scheduledRest;
    let expectedRest: number;
    let badRest: number;
    let probability: number | null;
    if (c.pastRest.length >= 3) {
      const w = c.pastRest.map((_, i) => 0.5 ** (i / HALF_LIFE_MONTHS));
      const tot = w.reduce((s, x) => s + x, 0);
      probability = c.pastRest.reduce((s, r, i) => (r + sched > c.available ? s + w[i]! : s), 0) / tot;
      expectedRest = weightedQuantile(c.pastRest, w, 0.5) + sched;
      badRest = weightedQuantile(c.pastRest, w, 0.9) + sched;
    } else {
      // Not enough history: assume the pace so far continues.
      const pace = day > 0 ? Math.round((c.spent / day) * (days - day)) : 0;
      expectedRest = pace + sched;
      badRest = expectedRest;
      probability = null;
    }
    const alreadyOver = c.available < 0;
    if (alreadyOver) probability = 1;
    const overBy = Math.max(0, expectedRest - c.available);
    const risky = alreadyOver || (probability === null ? overBy > 0 : probability >= minProbability);
    if (!risky || (!alreadyOver && expectedRest === 0 && badRest === 0)) continue;
    out.push({
      id: c.id,
      name: c.name,
      available: c.available,
      spent: c.spent,
      expectedRest,
      badRest,
      overBy,
      probability,
      alreadyOver,
    });
  }
  return out.sort(
    (x, y) =>
      Number(y.alreadyOver) - Number(x.alreadyOver) ||
      (y.probability ?? 0.5) - (x.probability ?? 0.5) ||
      y.overBy - x.overBy,
  );
}
